<template>
    <div
        ref="container"
        class="image image-container"
        :style="computedStyle"
        :class="fill"
        @click="$emit('click')"
    >
        <div class="placeholder" :class="{ ready: clientLoaded }" />
        <img
            v-if="calcHeight > 0 && calcWidth > 0"
            :alt="alt || title"
            fetchpriority="high"
            draggable="false"
            loading="lazy"
            v-bind="computedSource"
        />
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUpdated, useNuxtApp } from '#imports';

const { $bp } = useNuxtApp();
defineEmits(['click']);

const clientLoaded = ref(false);
const calcWidth = ref(300);
const calcHeight = ref(300);
const container = ref(null);

const props = defineProps({
    id: { type: [Number, String], default: 0 },
    title: { type: String, default: '' },
    alt: { type: String, default: '' },
    name: { type: String, default: '' },
    ext: { type: String, default: '' },
    width: { type: Number, default: 0 },
    height: { type: Number, default: 0 },
    created: { type: String, default: '' },
    metadataValues: { type: Array, default: () => [] },
    focalPoint: { type: [Array, Object, null], default: () => [0.5, 0.5] },
    tags: { type: Array, default: () => [] },
    copyright: { type: Object, default: () => ({}) },
    licenses: { type: Array, default: () => [] },
    description: { type: String, default: '' },
    type: { type: String, default: '' },
    appVersion: { type: String, default: '' },
    generic_url: { type: String, default: '' }, // eslint-disable-line vue/prop-name-casing
    preview_url: { type: String, default: '' }, // eslint-disable-line vue/prop-name-casing
    dynamic_url: { type: String, default: '' }, // eslint-disable-line vue/prop-name-casing
    src: { type: String, default: '' },
    computeWidth: { type: Boolean, default: false },
    fill: { type: String, validator: v => ['cover', 'contain'].includes(v), default: 'contain' },
});

const computedStyle = computed(() => {
    if (container.value && props.computeWidth) return `width: ${Math.round(props.width / props.height * calcHeight.value)}px`;

    return '';
});

onMounted(() => {
    calcWidth.value = Math.round(container.value.getBoundingClientRect().width);
    calcHeight.value = Math.round(container.value.getBoundingClientRect().height);
    clientLoaded.value = true;

    if (props.computeWidth) calcWidth.value = props.width / props.height * calcHeight.value;
});

onUpdated(() => {
    calcWidth.value = Math.round(container.value.getBoundingClientRect().width);
    calcHeight.value = Math.round(container.value.getBoundingClientRect().height);
});

watch(() => JSON.stringify($bp.bps), () => {
    if (container.value) {
        calcWidth.value = Math.round(container.value.getBoundingClientRect().width);
        calcHeight.value = Math.round(container.value.getBoundingClientRect().height);
    }
});

const computedFocalPoint = computed(() => {
    if (props.focalPoint && props.focalPoint.length === 2) return props.focalPoint;

    return [0.5, 0.5];
});

const computedSource = computed(() => {
    const scales = [1, 1.5, 2];

    if (props.appVersion.match(/^2/) !== null) {
        return {
            srcset: scales.map(scale => `${props.src}?width=${Math.round(calcWidth.value * scale)}&height=${Math.round(calcHeight.value * scale)}&crop=fp&fp=${computedFocalPoint.value.join(',')}&fp_zoom=1&format=webp ${String(scale)}x`).join(','),
            src: `${props.src}?width=${calcWidth.value}&height=${calcHeight.value}&crop=fp&fp=0.5,0.5&fp_zoom=1&format=webp`,
        };
    }

    return {
        srcset: scales.map(scale => props.generic_url.replace(/\width=.*/, `width=${Math.round(calcWidth.value * scale)}&height=${Math.round(calcHeight.value * scale)}&crop=fp&fp=${computedFocalPoint.value.join(',')}&fp_zoom=1&format=webp ${String(scale)}x`)).join(','),
        src: props.generic_url.replace(/\width=.*/, `width=${calcWidth.value}&height=${calcHeight.value}&crop=fp&fp=${computedFocalPoint.value.join(',')}&fp_zoom=1&format=webp`),
    };
});
</script>

<script>
export default {
    name: 'FrontifyImage',
};
</script>

<style scoped>
.image-container {
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 100%;
}
.image-container .placeholder {
  pointer-events: none;
  transition: opacity 0.25s ease-out;
  position: absolute;
  background: var(--color-base-white);
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}
.image-container .placeholder.ready {
  opacity: 0;
}
.image-container.cover img {
  object-fit: cover;
}
.image-container.contain img {
  object-fit: contain;
}
.image-container > img {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  max-width: 100%;
}
</style>
